import React from "react";
import { BadgeCheck, Printer, Sparkles } from "lucide-react";

/** Uma linha da IA: **negrito**, listas com "-" e as citações [n] viram etiquetas. */
function linha(l, k) {
  const lista = /^\s*[-•]\s/.test(l);
  const partes = l.replace(/^\s*[-•]\s/, "").split(/(\*\*[^*]+\*\*|\[\d+\])/g).filter(Boolean).map((p, i) =>
    p.startsWith("**") ? <strong key={i}>{p.slice(2, -2)}</strong>
      : /^\[\d+\]$/.test(p) ? <sup key={i} className="mx-0.5 text-[10px] font-bold text-[#1E9E2A]">{p}</sup> : p);
  return lista ? <li key={k} className="ml-5 list-disc">{partes}</li> : <p key={k} className="mb-2">{partes}</p>;
}

const Paragrafos = ({ t }) => (t || "").split("\n").filter((l) => l.trim()).map(linha);

/** Ficha técnica que a IA escreve entre [FICHA] e [/FICHA] (título, finalidade, ingredientes, preparo, observações, onde aprender). */
export function lerFicha(bloco) {
  const f = { titulo: "", finalidade: "", ingredientes: [], preparo: [], observacoes: "", onde: "" };
  let secao = "";
  (bloco || "").split("\n").forEach((l) => {
    const t = l.trim();
    if (!t) return;
    const campo = (nome) => new RegExp(`^${nome}\\s*:\\s*(.*)$`, "i").exec(t);
    let m;
    if ((m = campo("T[ií]tulo"))) { f.titulo = m[1]; secao = ""; }
    else if ((m = campo("Finalidade"))) { f.finalidade = m[1]; secao = ""; }
    else if ((m = campo("Ingredientes"))) { secao = "ing"; if (m[1]) f.ingredientes.push(m[1]); }
    else if ((m = campo("Modo de preparo"))) { secao = "prep"; if (m[1]) f.preparo.push(m[1]); }
    else if ((m = campo("Observa[cç][oõ]es"))) { f.observacoes = m[1]; secao = "obs"; }
    else if ((m = campo("Onde aprender"))) { f.onde = m[1]; secao = ""; }
    else if (secao === "ing") f.ingredientes.push(t.replace(/^[-•]\s*/, ""));
    else if (secao === "prep") f.preparo.push(t.replace(/^\d+[.)]\s*/, ""));
    else if (secao === "obs") f.observacoes += " " + t;
  });
  return f;
}

const sem = (x) => (x || "").replace(/\s?\[\d+\]/g, "").replace(/\*\*/g, "");
const esc = (x) => sem(x).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
// "Ingrediente — quantidade" (ou "Ingrediente: quantidade")
const separa = (i) => { const t = sem(i); const p = t.split(/\s+[—–]\s+/); if (p.length > 1) return p; const k = t.lastIndexOf(": "); return k > 0 ? [t.slice(0, k), t.slice(k + 2)] : [t]; };

/** Abre a ficha numa janela própria para imprimir ou salvar em PDF. */
export function imprimirFicha(f, revisada) {
  const w = window.open("", "_blank");
  if (!w) return;
  const ing = f.ingredientes.map((i) => { const [n, q] = separa(i); return `<tr><td>${esc(n)}</td><td>${esc(q || "")}</td></tr>`; }).join("");
  w.document.write(`<!doctype html><html lang="pt-BR"><head><meta charset="utf-8"><title>${esc(f.titulo || "Ficha técnica")}</title>
<style>body{font-family:Arial,sans-serif;color:#0f172a;margin:32px;max-width:760px}h1{font-size:22px;margin:4px 0}.k{color:#1E9E2A;font-size:11px;letter-spacing:2px;font-weight:bold}
.selo{display:inline-block;margin-top:6px;font-size:12px;padding:3px 10px;border-radius:99px;${revisada ? "background:#dcfce7;color:#166534" : "background:#fef3c7;color:#92400e"}}
h2{font-size:14px;color:#232D4F;border-bottom:2px solid #1E9E2A;padding-bottom:3px;margin-top:20px}table{width:100%;border-collapse:collapse;font-size:13px}td{border-bottom:1px solid #e2e8f0;padding:6px 4px}
td:last-child{text-align:right;white-space:nowrap}ol{font-size:13px;padding-left:20px}li{margin:4px 0}p{font-size:13px;line-height:1.5}.pe{margin-top:30px;font-size:10px;color:#64748b;border-top:1px solid #e2e8f0;padding-top:6px}</style></head><body>
<div class="k">IEP INTELLIGENCE · FICHA TÉCNICA</div><h1>${esc(f.titulo || "Ficha técnica")}</h1>
<span class="selo">${revisada ? "✔ Revisada pela equipe" : "Gerada pela IA — confira com a aula"}</span>
${f.finalidade ? `<h2>Finalidade</h2><p>${esc(f.finalidade)}</p>` : ""}${ing ? `<h2>Ingredientes</h2><table>${ing}</table>` : ""}
${f.preparo.length ? `<h2>Modo de preparo</h2><ol>${f.preparo.map((p) => `<li>${esc(p)}</li>`).join("")}</ol>` : ""}
${f.observacoes ? `<h2>Observações</h2><p>${esc(f.observacoes)}</p>` : ""}${f.onde ? `<h2>Onde aprender</h2><p>${esc(f.onde)}</p>` : ""}
<div class="pe">IEP – In'Nova Educação Profissional · Método Cabeleireiro Científico Formulador · ${new Date().toLocaleDateString("pt-BR")}</div>
<script>window.onload=()=>setTimeout(()=>window.print(),300)</script></body></html>`);
  w.document.close();
}

export function FichaTecnica({ bloco, revisada = false, acoes = null, completa = true }) {
  const f = lerFicha(bloco);
  const titulo = (x) => <div className="text-[11px] font-bold uppercase tracking-wide text-[#232D4F]">{x}</div>;
  return (
    <div className="my-2 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="ia-ficha">
      <div className="flex items-start gap-2 px-3.5 py-2.5 text-white" style={{ background: "linear-gradient(135deg,#1A2340,#232D4F 60%,#1E9E2A)" }}>
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[#A7F3B0]" />
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#A7F3B0]">Ficha técnica</div>
          <div className="font-display font-bold leading-snug">{sem(f.titulo) || "Montando a ficha…"}</div>
        </div>
      </div>
      <div className="space-y-2.5 px-3.5 py-3 text-[14px] text-slate-700">
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${revisada ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>
          {revisada ? <><BadgeCheck className="h-3.5 w-3.5" /> Revisada pela equipe</> : "Gerada pela IA · confira com a aula"}
        </span>
        {f.finalidade && <div>{titulo("Finalidade")}{sem(f.finalidade)}</div>}
        {f.ingredientes.length > 0 && (
          <div>{titulo("Ingredientes")}
            <table className="w-full text-[13px]"><tbody>{f.ingredientes.map((i, k) => { const [n, q] = separa(i); return (
              <tr key={k} className="border-b border-slate-100"><td className="py-1 pr-2">{n}</td><td className="whitespace-nowrap py-1 text-right font-semibold text-slate-800">{q}</td></tr>); })}</tbody></table></div>
        )}
        {f.preparo.length > 0 && <div>{titulo("Modo de preparo")}<ol className="ml-5 list-decimal space-y-1 text-[13px]">{f.preparo.map((p, k) => <li key={k}>{sem(p)}</li>)}</ol></div>}
        {f.observacoes && <div>{titulo("Observações")}<p className="text-[13px]">{sem(f.observacoes)}</p></div>}
        {f.onde && <div className="rounded-lg bg-slate-50 px-2.5 py-2 text-[13px]"><b className="text-[#1E9E2A]">Onde aprender:</b> {sem(f.onde)}</div>}
      </div>
      {completa && (
        <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 px-3.5 py-2">
          {acoes}
          <button type="button" onClick={() => imprimirFicha(f, revisada)} className="ml-auto inline-flex items-center gap-1 text-[12px] font-semibold text-slate-600 hover:text-slate-900"><Printer className="h-3.5 w-3.5" /> Imprimir / PDF</button>
        </div>
      )}
    </div>
  );
}

/** Texto da IA. Se tiver ficha técnica ([FICHA]...[/FICHA]), mostra o cartão no lugar do bloco (mesmo enquanto ainda está sendo escrita). */
export function Texto({ t, revisada = false, acoesFicha = null }) {
  const s = t || "";
  const i = s.indexOf("[FICHA]");
  if (i < 0) return <div className="text-[15px] leading-relaxed text-slate-700"><Paragrafos t={s} /></div>;
  const j = s.indexOf("[/FICHA]", i);
  const antes = s.slice(0, i), bloco = s.slice(i + 7, j < 0 ? undefined : j), depois = j < 0 ? "" : s.slice(j + 8);
  return (
    <div className="text-[15px] leading-relaxed text-slate-700">
      <Paragrafos t={antes} />
      <FichaTecnica bloco={bloco} revisada={revisada} acoes={acoesFicha} completa={j >= 0} />
      <Paragrafos t={depois} />
    </div>
  );
}
